import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  MoreHorizontal,
  Square,
  CheckSquare,
  Star,
  UserPlus,
  ArrowRight,
  Copy,
  Trash2,
  Calendar,
  UserCircle,
  Plus,
  Flag,
  ListChecks,
} from "lucide-react";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Textarea } from "../../../components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { cn } from "../../../lib/utils";
import { useSupabase } from "../../../contexts/SupabaseContext";
import { useAuth } from "../../../contexts/AuthContext";
import type { Task, TaskPriority, TaskStatus } from "../../../types/database";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

/**
 * Temporary subtask object used only during parent-task creation.
 * We don't have an id yet because the subtask isn't persisted until the
 * parent task is saved, so we keep only the editable fields.
 */
interface TempSubtask {
  text: string;
  assigned_to?: string;
  due_date?: string;
  priority?: TaskPriority;
}

const TaskList: React.FC = () => {
  const {
    tasks: allTasks,
    loadTasks,
    createTask,
    updateTask,
    deleteTask,
    promoteTaskToProject,
  } = useSupabase();
  const { user } = useAuth();

  // Local state mirrors supabase tasks filtered for category "task" & parentless.
  const [taskList, setTaskList] = useState<Task[]>([]);
  const [showCompletedAtBottom, setShowCompletedAtBottom] = useState(false);

  // Creation related state
  const [isCreating, setIsCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newAssignee, setNewAssignee] = useState("");
  const [newPriority, setNewPriority] = useState<TaskPriority>("medium");
  const [newDueDate, setNewDueDate] = useState("");

  // Subtask temp
  const [tempSubtasks, setTempSubtasks] = useState<TempSubtask[]>([]);
  const [newTempSubText, setNewTempSubText] = useState("");

  // UI states
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [assigningTaskId, setAssigningTaskId] = useState<string | null>(null);
  const [assignInput, setAssignInput] = useState("");
  const [settingDueDateTaskId, setSettingDueDateTaskId] = useState<string | null>(null);
  const [dueDateInput, setDueDateInput] = useState("");

  // Inline subtask editing state
  const [editingSubtaskId, setEditingSubtaskId] = useState<string | null>(null);
  const [subAssignInput, setSubAssignInput] = useState("");
  const [subDueInput, setSubDueInput] = useState("");

  // Summary / filter state (Phase‑1 additions)
  type FilterKey = "active" | "inProgress" | "today" | "overdue" | "done";
  const [activeFilter, setActiveFilter] = useState<FilterKey>("active");
  const [showCompleted, setShowCompleted] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);

  // Ref to creation card for outside‑click detection
  const createCardRef = useRef<HTMLDivElement>(null);

  // Load tasks on mount
  useEffect(() => {
    loadTasks("task");
  }, [loadTasks]);

  // Filter parent tasks only (no parent_id)
  useEffect(() => {
    setTaskList(
      allTasks
        .filter((t) => t.category === "task" && !t.parent_id)
        .sort((a, b) => {
          // High priority first, then created date.
          const priorityOrder = { high: 0, medium: 1, low: 2 } as const;
          return (
            (priorityOrder[a.priority ?? "medium"] -
              priorityOrder[b.priority ?? "medium"]) ||
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        })
    );
  }, [allTasks]);

  // click outside close menu
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (activeMenuId && menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setActiveMenuId(null);
      }
      // Auto-save/close expanded task on outside click
      if (editingTaskId && !(e.target as HTMLElement).closest(".task-card")) {
        setEditingTaskId(null);
      }

      // Prevent closing if click is on a select dropdown or trigger
      const target = e.target as HTMLElement;
      if (
        target.closest('.select-trigger') ||
        target.closest('.select-content')
      ) {
        return;
      }

      // Handle outside click for creation card
      if (
        isCreating &&
        createCardRef.current &&
        !createCardRef.current.contains(e.target as Node)
      ) {
        // If title not empty, submit
        if (newTitle.trim()) {
          handleCreateFinalize();
        } else {
          // cancel
          setIsCreating(false);
        }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [activeMenuId, editingTaskId, isCreating, newTitle]);

  // Helpers
  const formatDate = (d: string) => {
    return new Date(d).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
    });
  };

  const createSubtaskObjects = async (parentId: string) => {
    for (const st of tempSubtasks) {
      await createTask({
        title: st.text,
        category: "task",
        status: "todo",
        parent_id: parentId,
        priority: st.priority ?? "medium",
        // optional
        assigned_to: st.assigned_to,
        due_date: st.due_date,
      });
    }
    setTempSubtasks([]);
  };

  const handleCreateFinalize = async () => {
    if (!newTitle.trim() || !user) return;

    const payload: {
      title: string;
      description?: string;
      assigned_to?: string;
      category: "task";
      status: "todo";
      priority?: "low" | "medium" | "high";
      user_id: string;
      due_date?: string;
    } = {
      title: newTitle,
      category: "task",
      status: "todo",
      user_id: user.id,
    };
    if (newDescription.trim()) payload.description = newDescription.trim();
    if (newAssignee.trim()) payload.assigned_to = newAssignee.trim();
    payload.priority = newPriority;
    if (newDueDate.trim()) payload.due_date = newDueDate.trim();

    try {
      const newTask = await createTask(payload);
      await createSubtaskObjects(newTask.id);
      setNewTitle("");
      setNewDescription("");
      setNewAssignee("");
      setNewTempSubText("");
      setTempSubtasks([]);
      setIsCreating(false);
    } catch (err) {
      console.error("Task creation failed", err);
      alert("Failed to save task. Check console for details.");
      return;
    }
  };

  const handleCreateKeyPress = async (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && newTitle.trim()) {
      handleCreateFinalize();
    }
  };

  const toggleComplete = async (task: Task) => {
    await updateTask(task.id, { status: task.status === "completed" ? "todo" : "completed" });
    setShowCompletedAtBottom(false); // keep completed in place for now
  };

  const toggleStar = async (task: Task) => {
    await updateTask(task.id, {
      priority: task.priority === "high" ? "medium" : "high",
    });
  };

  const handleDelete = async (task: Task) => {
    await deleteTask(task.id);
  };

  const handleDuplicate = async (task: Task) => {
    await createTask({
      ...task,
      id: undefined as never, // id will be generated server side
      title: `${task.title} (copy)`,
      status: "todo",
      priority: task.priority ?? "medium",
    });
  };

  const saveDescription = async (task: Task, desc: string) => {
    await updateTask(task.id, { description: desc });
  };

  const subtasksFor = (id: string) => taskList.filter((t) => t.parent_id === id);

  // UI components helpers
  const renderSubtasks = (parent: Task) => {
    const list = subtasksFor(parent.id);
    return (
      <div className="space-y-1 mt-2 ml-6">
        {list.map((st) => {
          const isEditing = editingSubtaskId === st.id;
          return (
            <Card key={st.id} className="bg-muted/50 task-card">
              <CardContent className="p-2 flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-0 w-5 h-5 hover:bg-muted"
                  onClick={() => toggleComplete(st)}
                >
                  {st.status === "completed" ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                </Button>

                {/* Title or editable input */}
                {isEditing ? (
                  <Input
                    value={st.title}
                    onChange={(e) => updateTask(st.id, { title: e.target.value })}
                    className="text-sm flex-1 h-7"
                  />
                ) : (
                  <span
                    className={cn("text-sm flex-1", st.status === "completed" && "line-through")}
                    onClick={() => {
                      setEditingSubtaskId(st.id);
                      setSubAssignInput(st.assigned_to ?? "");
                      setSubDueInput(st.due_date ?? "");
                    }}
                  >
                    {st.title}
                  </span>
                )}

                {/* Assignee & due date chips */}
                {!isEditing && (
                  <>
                    {st.assigned_to && (
                      <span className="text-[10px] bg-muted px-1 rounded-sm">{st.assigned_to.split("@")[0]}</span>
                    )}
                    {st.due_date && (
                      <span className="text-[10px] text-muted-foreground ml-1">{formatDate(st.due_date)}</span>
                    )}
                  </>
                )}

                {/* Edit panel */}
                {isEditing && (
                  <div className="flex items-center gap-1">
                    <Input
                      type="email"
                      value={subAssignInput}
                      onChange={(e) => setSubAssignInput(e.target.value)}
                      placeholder="assignee"
                      className="h-6 text-[10px] w-24"
                    />
                    <Input
                      type="date"
                      value={subDueInput}
                      onChange={(e) => setSubDueInput(e.target.value)}
                      className="h-6 text-[10px] w-24"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-1"
                      onClick={async () => {
                        await updateTask(st.id, {
                          assigned_to: subAssignInput || undefined,
                          due_date: subDueInput || undefined,
                        });
                        setEditingSubtaskId(null);
                      }}
                    >
                      Save
                    </Button>
                  </div>
                )}

                <Button
                  variant="ghost"
                  size="sm"
                  className="p-0 w-5 h-5 hover:bg-muted"
                  onClick={() => handleDelete(st)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
        {/* add subtask input */}
        <div className="flex items-center gap-2 mt-1">
          <Plus className="w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Add subtask..."
            value={newTempSubText}
            onChange={(e) => setNewTempSubText(e.target.value)}
            onKeyPress={async (e) => {
              if (e.key === "Enter" && newTempSubText.trim()) {
                await createTask({
                  title: newTempSubText,
                  category: "task",
                  status: "todo",
                  parent_id: parent.id,
                });
                setNewTempSubText("");
              }
            }}
            className="h-7 px-2 py-1 text-xs"
          />
        </div>
      </div>
    );
  };

  // Helper function to check if older than 30 days
  const isOlderThan30Days = (dateString: string): boolean => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      // Set time to 0 to compare dates only
      date.setHours(0, 0, 0, 0);
      now.setHours(0, 0, 0, 0);
      const diffTime = now.getTime() - date.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 30;
    } catch (error) {
      console.error("Error calculating date difference:", dateString, error);
      return false;
    }
  };

  // Helper function to check if task is overdue
  const isOverdue = useCallback((task: Task): boolean => {
    if (task.status === 'completed') return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Compare date part only

    if (task.due_date) {
      try {
        const dueDate = new Date(task.due_date);
         // Adjust for potential timezone issues if the date string doesn't include timezone info
        const userTimezoneOffset = dueDate.getTimezoneOffset() * 60000;
        const adjustedDueDate = new Date(dueDate.getTime() + userTimezoneOffset);
        adjustedDueDate.setHours(0, 0, 0, 0);
        return adjustedDueDate < today;
      } catch (error) {
        console.error("Error parsing due date:", task.due_date, error);
        // Fallback to createdAt logic if due_date is invalid
        return isOlderThan30Days(task.created_at);
      }
    }
    // If no due date, check if created more than 30 days ago
    return isOlderThan30Days(task.created_at);
  }, []);

  // Summary counts (recomputed when taskList changes)
  const taskSummary = useMemo(() => {
    return {
      active: taskList.filter((t) => t.status === "todo").length,
      inProgress: taskList.filter((t) => t.status === "in-progress").length,
      today: taskList.filter((t) => t.priority === "high" && t.status !== "completed").length,
      overdue: taskList.filter(isOverdue).length,
      done: taskList.filter((t) => t.status === "completed").length,
    } as const;
  }, [taskList, isOverdue]);

  // Filtered list helper
  const getFilteredTasks = () => {
    if (activeFilter === "done" && showCompleted) {
      return taskList.filter((t) => t.status === "completed");
    }

    const filterMap: Record<FilterKey, (t: Task) => boolean> = {
      active: (t) => t.status === "todo",
      inProgress: (t) => t.status === "in-progress",
      today: (t) => t.priority === "high" && t.status !== "completed",
      overdue: (t) => isOverdue(t),
      done: () => false, // handled above
    };

    return taskList.filter(filterMap[activeFilter]);
  };

  // Sort tasks: most recent (created_at) first, completed at bottom
  const sortTasks = (list: Task[]) => {
    if (!showCompletedAtBottom) {
      // Keep completed tasks in place
      return [...list].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }
    // Otherwise, move completed to bottom
    return [...list].sort((a, b) => {
      if (a.status === 'completed' && b.status !== 'completed') return 1;
      if (a.status !== 'completed' && b.status === 'completed') return -1;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  };

  // ---------------------
  // Temp-subtask helpers
  // ---------------------

  const addTempSubtask = () => {
    if (!newTempSubText.trim()) return;
    setTempSubtasks((prev) => [
      ...prev,
      {
        text: newTempSubText.trim(),
      },
    ]);
    setNewTempSubText("");
  };

  const updateTempSubtask = (index: number, field: keyof TempSubtask, value: string) => {
    setTempSubtasks((prev) => {
      const copy = [...prev];
      (copy[index] as any)[field] = value;
      return copy;
    });
  };

  const removeTempSubtask = (index: number) => {
    setTempSubtasks((prev) => prev.filter((_, idx) => idx !== index));
  };

  // When user navigates away, set showCompletedAtBottom to true
  useEffect(() => {
    const handleVisibility = () => {
      // If the document is hidden (user switched tab or navigated away), next render will move completed to bottom
      if (document.hidden) setShowCompletedAtBottom(true);
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Summary buttons */}
      <div className="grid grid-cols-5 gap-2 mb-6">
        {/* Active */}
        <button
          onClick={() => setActiveFilter("active")}
          className={cn(
            "flex flex-col items-center justify-center p-3 rounded-lg border transition-colors",
            "bg-green-500/10 hover:bg-green-500/20", // Always green background
            activeFilter === "active"
              ? "border-green-500" // Stronger border when active
              : "border-green-500/20 hover:border-green-500/40" // Lighter border otherwise
          )}
        >
          <span className="text-2xl font-semibold">{taskSummary.active}</span>
          <span className="text-sm text-muted-foreground">Active</span>
        </button>

        {/* In Progress */}
        <button
          onClick={() => setActiveFilter("inProgress")}
          className={cn(
            "flex flex-col items-center justify-center p-3 rounded-lg border transition-colors",
            "bg-blue-500/10 hover:bg-blue-500/20", // Always blue background
            activeFilter === "inProgress"
              ? "border-blue-500" // Stronger border when active
              : "border-blue-500/20 hover:border-blue-500/40" // Lighter border otherwise
          )}
        >
          <span className="text-2xl font-semibold">{taskSummary.inProgress}</span>
          <span className="text-sm text-muted-foreground">In Progress</span>
        </button>

        {/* Do Today */}
        <button
          onClick={() => setActiveFilter("today")}
          className={cn(
            "flex flex-col items-center justify-center p-3 rounded-lg border transition-colors",
            "bg-warning/10 hover:bg-warning/20", // Always warning background
            activeFilter === "today"
              ? "border-warning" // Stronger border when active
              : "border-warning/20 hover:border-warning/40" // Lighter border otherwise
          )}
        >
          <span className="text-2xl font-semibold">{taskSummary.today}</span>
          <span className="text-sm text-muted-foreground">Do Today</span>
        </button>

        {/* Overdue */}
        <button
          onClick={() => setActiveFilter("overdue")}
          className={cn(
            "flex flex-col items-center justify-center p-3 rounded-lg border transition-colors",
            "bg-destructive/10 hover:bg-destructive/20", // Always destructive background
            activeFilter === "overdue"
              ? "border-destructive" // Stronger border when active
              : "border-destructive/20 hover:border-destructive/40" // Lighter border otherwise
          )}
        >
          <span className="text-2xl font-semibold">{taskSummary.overdue}</span>
          <span className="text-sm text-muted-foreground">Overdue</span>
        </button>

        {/* Done */}
        <button
          onClick={() => {
            setActiveFilter("done");
            setShowCompleted((prev) => !prev);
          }}
          className={cn(
            "flex flex-col items-center justify-center p-3 rounded-lg border transition-colors",
            "bg-muted/30 hover:bg-muted/50", // Always muted background
             (activeFilter === "done" && showCompleted)
              ? "border-slate-500" // Stronger border when active (using slate as muted is less distinct)
              : "border-muted hover:border-slate-400" // Default border otherwise
          )}
        >
          <span className="text-2xl font-semibold">{showCompleted ? taskSummary.done : "—"}</span>
          <span className="text-sm text-muted-foreground">Done</span>
        </button>
      </div>

      {/* Create Card */}
      <Card
        ref={createCardRef}
        className={cn(
          "todo-card mb-4"
        )}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <ListChecks className="w-5 h-5 mt-1 text-muted-foreground" />
            <div className="flex-1 flex flex-col gap-2">
              <Input
                placeholder="What do you need to do?"
                value={newTitle}
                onFocus={() => { if (!isCreating) setIsCreating(true); }}
                onClick={() => { if (!isCreating) setIsCreating(true); }}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyPress={handleCreateKeyPress}
                className="border-0 p-0 h-auto text-sm focus-visible:ring-0"
              />
              {isCreating && (
                <>
                  <Textarea
                    placeholder="Description..."
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    className="border rounded-md p-2 text-sm dark:bg-muted/40"
                  />
                  <div className="flex items-center gap-2 w-full">
                    <Select value={newPriority} onValueChange={(value) => setNewPriority(value as TaskPriority)}>
                      <SelectTrigger className="select-trigger w-auto h-auto text-xs p-1 px-2 bg-muted/50 border-0 focus:ring-0">
                        <SelectValue placeholder="Priority" />
                      </SelectTrigger>
                      <SelectContent className="select-content">
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                    <UserCircle className="w-4 h-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Assign to"
                      value={newAssignee}
                      onChange={(e) => setNewAssignee(e.target.value)}
                      onKeyPress={handleCreateKeyPress}
                      className="border-0 bg-transparent text-sm h-auto p-0 focus-visible:ring-0 dark:text-slate-200"
                      style={{ width: '24ch' }}
                      maxLength={64}
                    />
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <Input
                      type="date"
                      value={newDueDate}
                      onChange={(e) => setNewDueDate(e.target.value)}
                      onKeyPress={handleCreateKeyPress}
                      className="border-0 bg-transparent text-xs h-auto p-0 focus-visible:ring-0 w-[110px]"
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      onClick={handleCreateFinalize}
                      disabled={!newTitle.trim()}
                      size="sm"
                    >
                      Save
                    </Button>
                  </div>

                  {/* -------------------- */}
                  {/* Temp Subtasks Editor */}
                  {/* -------------------- */}

                  <div className="mt-4 space-y-2">
                    {tempSubtasks.map((st, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-muted/40 p-2 rounded-md">
                        <Input
                          value={st.text}
                          onChange={(e) => updateTempSubtask(idx, "text", e.target.value)}
                          placeholder="Subtask title"
                          className="h-7 text-xs flex-1"
                        />
                        <Input
                          type="email"
                          value={st.assigned_to ?? ""}
                          onChange={(e) => updateTempSubtask(idx, "assigned_to", e.target.value)}
                          placeholder="Assignee"
                          className="h-7 text-xs w-36"
                        />
                        <Input
                          type="date"
                          value={st.due_date ?? ""}
                          onChange={(e) => updateTempSubtask(idx, "due_date", e.target.value)}
                          className="h-7 text-xs w-32"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-0 w-5 h-5 hover:text-destructive"
                          onClick={() => removeTempSubtask(idx)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}

                    {/* Add new subtask input */}
                    <div className="flex items-center gap-2">
                      <Input
                        placeholder="Add subtask..."
                        value={newTempSubText}
                        onChange={(e) => setNewTempSubText(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") addTempSubtask();
                        }}
                        className="h-7 text-xs flex-1"
                      />
                      <Button
                        size="sm"
                        disabled={!newTempSubText.trim()}
                        onClick={addTempSubtask}
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tasks list */}
      <div className="space-y-1 overflow-y-auto">
        {sortTasks(getFilteredTasks()).map((task) => {
          const subtasks = subtasksFor(task.id);
          return (
            <Card
              key={task.id}
              className={cn(
                "todo-card task-card transition-all cursor-pointer hover:ring-1 hover:ring-primary",
                task.status === "completed" && "border-primary/30 dark:bg-muted/30",
                task.priority === "high" && "bg-warning/5 dark:bg-warning/20 border-warning",
                editingTaskId === task.id && "bg-muted"
              )}
            >
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn("p-0 w-6 h-6 hover:bg-muted", task.status === "completed" && "text-blue-600", task.priority === "high" && task.status !== "completed" && "text-warning")}
                    onClick={() => toggleComplete(task)}
                  >
                    {task.status === "completed" ? <CheckSquare className="w-4 h-4" /> : task.priority === "high" ? <Star className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                  </Button>

                  <div className="flex-1 min-w-0" onClick={() => setEditingTaskId(editingTaskId === task.id ? null : task.id)}>
                    <p className={cn("text-sm", task.status === "completed" && "line-through text-muted-foreground")}>{task.title}</p>
                    {task.description && <p className="text-xs mt-1 text-muted-foreground line-clamp-2">{task.description}</p>}
                    {editingTaskId === task.id && (
                      <>
                        <Textarea
                          value={task.description ?? ""}
                          onChange={(e) => saveDescription(task, e.target.value)}
                          className="w-full border rounded-md p-2 text-sm mt-2 dark:bg-muted/50"
                        />
                        <div className="flex items-center gap-2 w-full mt-2">
                          <Select value={task.priority ?? "medium"} onValueChange={(value) => updateTask(task.id, { priority: value as TaskPriority })}>
                            <SelectTrigger className="select-trigger w-auto h-auto text-xs p-1 px-2 bg-muted/50 border-0 focus:ring-0">
                              <SelectValue placeholder="Priority" />
                            </SelectTrigger>
                            <SelectContent className="select-content">
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="medium">Medium</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                            </SelectContent>
                          </Select>
                          <UserCircle className="w-4 h-4 text-muted-foreground" />
                          <Input
                            type="text"
                            placeholder="Assign to"
                            value={task.assigned_to ?? ""}
                            onChange={(e) => updateTask(task.id, { assigned_to: e.target.value })}
                            className="border-0 bg-transparent text-sm h-auto p-0 focus-visible:ring-0 dark:text-slate-200"
                            style={{ width: '24ch' }}
                            maxLength={64}
                          />
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <Input
                            type="date"
                            value={task.due_date ?? ""}
                            onChange={(e) => updateTask(task.id, { due_date: e.target.value })}
                            className="border-0 bg-transparent text-xs h-auto p-0 focus-visible:ring-0 w-[110px]"
                          />
                        </div>
                        {/* Subtasks for expanded task */}
                        <div className="mt-4 space-y-2">
                          {subtasksFor(task.id).map((st) => renderSubtasks(task))}
                          <div className="flex items-center gap-2 mt-1">
                            <Plus className="w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder="Add subtask..."
                              value={newTempSubText}
                              onChange={(e) => setNewTempSubText(e.target.value)}
                              onKeyPress={async (e) => {
                                if (e.key === "Enter" && newTempSubText.trim()) {
                                  await createTask({
                                    title: newTempSubText,
                                    category: "task",
                                    status: "todo",
                                    parent_id: task.id,
                                  });
                                  setNewTempSubText("");
                                }
                              }}
                              className="h-7 px-2 py-1 text-xs"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Button
                            onClick={() => setEditingTaskId(null)}
                            size="sm"
                          >
                            Save
                          </Button>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Date & menu */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{formatDate(task.created_at)}</span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="p-0 w-6 h-6 hover:bg-muted">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48" ref={menuRef}>
                        <DropdownMenuItem onClick={() => toggleStar(task)}>
                          <Star className={cn("w-4 h-4 mr-2", task.priority === "high" && "text-warning")}
                            fill={task.priority === "high" ? "currentColor" : "none"}
                          />
                          {task.priority === "high" ? "Remove from Today" : "Do Today"}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onSelect={(e) => {
                            e.preventDefault();
                            setAssigningTaskId(task.id);
                            setAssignInput("");
                          }}
                          className={cn(assigningTaskId === task.id && "bg-muted")}
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Assign
                        </DropdownMenuItem>
                        {assigningTaskId === task.id && (
                          <div className="px-4 py-2">
                            <Input
                              placeholder="Enter email"
                              value={assignInput}
                              onChange={(e) => setAssignInput(e.target.value)}
                              onKeyPress={async (e) => {
                                if (e.key === "Enter" && assignInput.trim()) {
                                  await updateTask(task.id, { assigned_to: assignInput.trim() });
                                  setAssigningTaskId(null);
                                }
                              }}
                              autoFocus
                            />
                          </div>
                        )}
                        <DropdownMenuItem onClick={() => promoteTaskToProject(task.id)}>
                          <ArrowRight className="w-4 h-4 mr-2" />
                          Promote to Project
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicate(task)}>
                          <Copy className="w-4 h-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => handleDelete(task)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          {formatDate(task.created_at)}
                        </DropdownMenuLabel>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                {subtasks.length > 0 && renderSubtasks(task)}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default TaskList; 